import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  DocumentTextIcon,
  TagIcon,
  UserIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  UserGroupIcon,
  BoltIcon,
  BeakerIcon,
  CogIcon,
  WrenchIcon,
  ShieldCheckIcon,
  ArrowsRightLeftIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentIcon,
  PhotoIcon,
  PencilIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from '../ConfirmationModal';
import DocumentDetailModal from './DocumentDetailModal';
import InvoiceMatcher from '../InvoiceMatcher';

interface StatementDetailModalProps {
  statementId: string;
  onClose: () => void;
  onStatementUpdate?: (statement: any) => void;
  onStatementDelete?: (statementId: string) => void;
}

interface Category {
  id: number;
  kategorie_id: number;
  betrag: number;
  member_id?: number;
  category_name?: string;
  member_name?: string;
  beitrag_jahr?: number;
}

interface Document {
  id: number;
  name: string;
  invoice_amount: number;
  upload_date: string;
  rechnung_pfad?: string;
  betrag?: number;
  file_type?: string;
  file_size?: number;
}

const StatementDetailModal: React.FC<StatementDetailModalProps> = ({
  statementId,
  onClose,
  onStatementUpdate,
  onStatementDelete
}) => {
  const [statement, setStatement] = useState<any>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [categoryMasters, setCategoryMasters] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // UI State
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState(0);
  const [categoryAmount, setCategoryAmount] = useState('');
  const [categoryMemberId, setCategoryMemberId] = useState<number | null>(null);
  const [categoryBeitragJahr, setCategoryBeitragJahr] = useState<number>(new Date().getFullYear());
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const [showInvoiceMatcher, setShowInvoiceMatcher] = useState(false);
  const [showDocumentDetail, setShowDocumentDetail] = useState<number | null>(null);
  const [statementTags, setStatementTags] = useState<string[]>([]);
  const [availableBuchungTags, setAvailableBuchungTags] = useState<string[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState({
    buchungstag: '',
    buchungstext: '',
    verwendungszweck: '',
    name_zahlungsbeteiligter: '',
    betrag: '',
    erstellt_am: '',
  });

  const categoryDropdownRef = useRef<HTMLDivElement>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const sortedCategories = [...categoryMasters].sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const sortedMembers = [...members].sort((a, b) => `${a.vorname} ${a.name}`.localeCompare(`${b.vorname} ${b.name}`, 'de'));

  useEffect(() => {
    if (!showCategoryDropdown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key.length !== 1 || !key.match(/[a-zäöüß]/)) return;
      const container = categoryDropdownRef.current;
      if (!container) return;
      const match = sortedCategories.findIndex(cat => cat.name.toLowerCase().startsWith(key));
      if (match >= 0) {
        const el = container.children[match] as HTMLElement;
        el?.scrollIntoView({ block: 'nearest' });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCategoryDropdown, sortedCategories]);

  useEffect(() => {
    if (!showMemberDropdown) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key.length !== 1 || !key.match(/[a-zäöüß]/)) return;
      const container = memberDropdownRef.current;
      if (!container) return;
      const match = sortedMembers.findIndex(m => `${m.vorname} ${m.name}`.toLowerCase().startsWith(key));
      if (match >= 0) {
        const el = container.children[match] as HTMLElement;
        el?.scrollIntoView({ block: 'nearest' });
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showMemberDropdown, sortedMembers]);

  // ConfirmationModal State
  const [confirmModal, setConfirmModal] = useState({
    visible: false,
    type: 'warning' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'two' as 'single' | 'two',
  });


  useEffect(() => {
    if (statementId) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [statementId]);

  useEffect(() => {
    if (statementId) {
      loadStatementDetails();
      loadCategories();
      loadMembers();
      loadAccounts();
      loadBuchungTags();
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [statementId]);

  const loadStatementDetails = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      setStatementTags(response.data.tags || []);
    } catch (err) {
      console.error('Error loading statement details:', err);
      setError('Buchungsdetails konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const loadBuchungTags = async () => {
    try {
      const response = await apiClient.get('/api/finance/buchung-tags');
      setAvailableBuchungTags((response.data || []).map((t: any) => t.tag));
    } catch (err) {
      console.error('Error loading buchung tags:', err);
    }
  };

  const handleAddTag = async (tag: string) => {
    if (!tag.trim() || statementTags.includes(tag.trim())) return;
    const trimmed = tag.trim();
    setStatementTags(prev => [...prev, trimmed]);
    try {
      await apiClient.post(`/api/finance/statements/${statementId}/tags`, { tag: trimmed });
      if (!availableBuchungTags.includes(trimmed)) {
        setAvailableBuchungTags(prev => [...prev, trimmed]);
      }
      // Notify parent about update
      if (onStatementUpdate && statement) {
        onStatementUpdate({ ...statement, tags: [...statementTags, trimmed] });
      }
    } catch (err) {
      console.error('Error adding tag:', err);
      setStatementTags(prev => prev.filter(t => t !== trimmed));
    }
  };

  const handleRemoveTag = async (tag: string) => {
    setStatementTags(prev => prev.filter(t => t !== tag));
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/tags/${encodeURIComponent(tag)}`);
      if (onStatementUpdate && statement) {
        onStatementUpdate({ ...statement, tags: statementTags.filter(t => t !== tag) });
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      setStatementTags(prev => [...prev, tag]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiClient.get('/api/finance/categories');
      setCategoryMasters(response.data);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  const loadMembers = async () => {
    try {
      const response = await apiClient.get('/api/finance/members');
      setMembers(response.data);
    } catch (err) {
      console.error('Failed to load members:', err);
    }
  };

  const loadAccounts = async () => {
    try {
      const response = await apiClient.get('/api/finance/accounts');
      setAccounts(response.data);
    } catch (err) {
      console.error('Failed to load accounts:', err);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  // Format currency with +/- sign and EUR suffix
  const formatAmount = (amount: number) => {
    const absAmount = Math.abs(amount);
    const formatted = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(absAmount);
    const sign = amount < 0 ? '-' : '+';
    return `${sign}${formatted} EUR`;
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

  const getRemainingAmount = useCallback(() => {
    if (!statement) return 0;
    const totalCategorized = categories.reduce((sum, cat) => sum + cat.betrag, 0);
    return Math.abs(statement.betrag) - totalCategorized;
  }, [statement, categories]);

  const isTransfer = useCallback(() => {
    return statement && statement.von_konto_id && statement.nach_konto_id;
  }, [statement]);

  const getAccountName = useCallback((accountId: number) => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? account.name : 'Unbekanntes Konto';
  }, [accounts]);

  const getCategoryIcon = useCallback((kategorie_id: number) => {
    const iconMap: { [key: number]: any } = {
      1: UserGroupIcon,
      2: BoltIcon,
      3: BeakerIcon,
      4: CogIcon,
      5: WrenchIcon,
      6: ShieldCheckIcon,
      14: ArrowsRightLeftIcon,
    };
    return iconMap[kategorie_id] || TagIcon;
  }, []);

  const getCategoryName = useCallback((kategorie_id: number) => {
    const category = categoryMasters.find(cat => cat.id === kategorie_id);
    return category ? category.name : `Kategorie ${kategorie_id}`;
  }, [categoryMasters]);

  const getMemberName = useCallback((member_id: number) => {
    const member = members.find(m => m.member_id === member_id);
    return member ? `${member.vorname} ${member.name}` : `Mitglied ${member_id}`;
  }, [members]);

  const addCategory = async () => {
    if (!selectedCategoryId || !categoryAmount) return;
    if (selectedCategoryId === 1 && !categoryMemberId) return;

    const amount = parseFloat(categoryAmount);
    const remainingAmount = getRemainingAmount();

    if (amount > remainingAmount) {
      alert(`Betrag überschreitet den verfügbaren Restbetrag`);
      return;
    }

    try {
      await apiClient.post(`/api/finance/statements/${statementId}/categories`, {
        kategorie_id: selectedCategoryId,
        betrag: amount,
        member_id: categoryMemberId,
        beitrag_jahr: selectedCategoryId === 1 ? categoryBeitragJahr : null,
      });

      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      if (onStatementUpdate) {
        onStatementUpdate(response.data);
      }

      setIsAddingCategory(false);
      setSelectedCategoryId(0);
      setCategoryAmount('');
      setCategoryMemberId(null);
      setCategoryBeitragJahr(new Date().getFullYear());
      setShowCategoryDropdown(false);
      setShowMemberDropdown(false);
    } catch (err) {
      console.error('Error adding category:', err);
    }
  };

  const deleteCategory = async (categoryId: number) => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/categories/${categoryId}`);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      if (onStatementUpdate) {
        onStatementUpdate(response.data);
      }
    } catch (err) {
      console.error('Error deleting category:', err);
    }
  };

  const deleteDocument = async (documentId: number) => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}/documents/${documentId}`);
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      if (onStatementUpdate) {
        onStatementUpdate(response.data);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
    }
  };

  const handleDeleteClick = () => {
    setConfirmModal({
      visible: true,
      type: 'warning',
      title: 'Buchung löschen?',
      message: 'Diese Aktion kann nicht rückgängig gemacht werden.',
      variant: 'two',
    });
  };

  const confirmDeleteStatement = async () => {
    try {
      await apiClient.delete(`/api/finance/statements/${statementId}`);
      if (onStatementDelete) onStatementDelete(statementId);

      setConfirmModal({
        visible: true,
        type: 'success',
        title: 'Erfolgreich',
        message: 'Die Buchung wurde erfolgreich gelöscht.',
        variant: 'single',
      });

      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error deleting statement:', err);
      setConfirmModal({
        visible: true,
        type: 'error',
        title: 'Fehler',
        message: 'Die Buchung konnte nicht gelöscht werden.',
        variant: 'single',
      });
    }
  };

  const handleDocumentClick = (documentId: number) => {
    setShowDocumentDetail(documentId);
  };

  const handleDocumentDelete = async (documentId: number) => {
    await deleteDocument(documentId);
    setShowDocumentDetail(null);
  };

  const handleDocumentTagAdd = async () => {
    await loadStatementDetails();
  };

  const handleDocumentTagDelete = async () => {
    await loadStatementDetails();
  };

  const handleDocumentAIScan = async () => {
    await loadStatementDetails();
  };

  const handleDocumentDownload = async (docId: number) => {
    const documentUri = `/api/documents/download/${docId}`;
    window.open(documentUri, '_blank');
  };

  const handleFilesSelected = async (files: any[]) => {
    try {
      await apiClient.post(`/api/finance/statements/${statementId}/documents`, {
        document_ids: files.map((doc) => doc.id)
      });
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      if (onStatementUpdate) {
        onStatementUpdate(response.data);
      }
      setShowInvoiceMatcher(false);
    } catch (err) {
      console.error('Error linking documents:', err);
    }
  };

  const toInputDate = (dateStr: string) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const enterEditMode = () => {
    if (!statement) return;
    setEditData({
      buchungstag: toInputDate(statement.buchungstag),
      buchungstext: statement.buchungstext || '',
      verwendungszweck: statement.verwendungszweck || '',
      name_zahlungsbeteiligter: statement.name_zahlungsbeteiligter || '',
      betrag: String(Math.abs(statement.betrag)),
      erstellt_am: toInputDate(statement.erstellt_am),
    });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setIsEditing(false);
  };

  const saveEdit = async () => {
    if (!statement) return;
    setIsSaving(true);
    try {
      const sign = statement.betrag < 0 ? -1 : 1;
      await apiClient.patch(`/api/finance/statements/${statementId}`, {
        buchungstag: editData.buchungstag,
        buchungstext: editData.buchungstext,
        verwendungszweck: editData.verwendungszweck,
        name_zahlungsbeteiligter: editData.name_zahlungsbeteiligter,
        betrag: sign * parseFloat(editData.betrag),
        erstellt_am: editData.erstellt_am,
      });
      // Reload statement
      const response = await apiClient.get(`/api/finance/statements/${statementId}`);
      setStatement(response.data);
      setCategories(response.data.categories || []);
      setDocuments(response.data.documents || []);
      if (onStatementUpdate) {
        onStatementUpdate(response.data);
      }
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving statement:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (!statement && !loading) return null;

  const isExpense = statement?.betrag < 0;
  const remainingAmount = getRemainingAmount();
  const totalCategorized = categories.reduce((sum, cat) => sum + cat.betrag, 0);

  // Status calculation
  const getStatus = () => {
    if (remainingAmount === 0 && totalCategorized > 0) return 'complete';
    if (totalCategorized > 0) return 'partial';
    return 'open';
  };
  const status = getStatus();

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4"
        onClick={handleClose}
      >
        <div
          className="w-full sm:max-w-lg bg-white dark:bg-[#1c2432] border-t sm:border border-gray-200 dark:border-gray-700 rounded-t-2xl sm:rounded-2xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700/50`}>
            <div className="flex items-center gap-3">
              {/* Transaction Icon */}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                isExpense ? 'bg-error-100 dark:bg-error-900/30' : 'bg-success-100 dark:bg-success-900/30'
              }`}>
                {isExpense ? (
                  <svg className="w-5 h-5 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18"/>
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
                  </svg>
                )}
              </div>
              <div>
                <h3 className="font-medium text-gray-900 dark:text-white">
                  {isExpense ? 'Auszahlung' : 'Einzahlung'}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {statement && formatDate(statement.buchungstag)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Status Badge */}
              {status === 'complete' && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-success-50 dark:bg-success-900/20 text-success-700 dark:text-success-400 font-medium">
                  Vollständig
                </span>
              )}
              {status === 'partial' && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-400 font-medium">
                  Teilweise
                </span>
              )}
              {status === 'open' && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium">
                  Offen
                </span>
              )}
              {/* Edit / Save Button */}
              {isEditing ? (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={cancelEdit}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Abbrechen"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={isSaving}
                    className="p-2 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors disabled:opacity-50"
                    title="Speichern"
                  >
                    <CheckIcon className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={enterEditMode}
                  className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title="Bearbeiten"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-200 border-t-primary-600 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Laden...</p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <ExclamationTriangleIcon className="h-12 w-12 text-error-600 dark:text-error-400 mx-auto mb-4" />
                  <p className="text-error-600 dark:text-error-400">{error}</p>
                  <button
                    onClick={loadStatementDetails}
                    className="mt-4 px-6 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors font-medium"
                  >
                    Erneut versuchen
                  </button>
                </div>
              </div>
            ) : statement ? (
              <div className="p-5 space-y-6">
                {/* Meta Information */}
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span>{statement.kontotyp || 'Bankkonto'}</span>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <span>Buchung: {formatDate(statement.buchungstag)}</span>
                </div>

                {/* Amount Display - Centered */}
                <div className="text-center py-4">
                  {isEditing ? (
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-2xl font-semibold ${isExpense ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'}`}>
                        {isExpense ? '-' : '+'}
                      </span>
                      <input
                        type="number"
                        value={editData.betrag}
                        onChange={(e) => setEditData({ ...editData, betrag: e.target.value })}
                        step="0.01"
                        className="w-40 text-center text-2xl font-semibold bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                      />
                      <span className="text-lg text-gray-400">EUR</span>
                    </div>
                  ) : (
                    <>
                      <p className={`text-3xl font-semibold ${isExpense ? 'text-error-600 dark:text-error-400' : 'text-success-600 dark:text-success-400'}`}>
                        {formatAmount(statement.betrag)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                        {isExpense ? 'Ausgang' : 'Eingang'}
                      </p>
                    </>
                  )}
                </div>

                {/* Buchungsdetails Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                    <InformationCircleIcon className="w-3.5 h-3.5" />
                    Buchungsdetails
                  </h4>
                  {isEditing ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Buchungsdatum</label>
                        <input
                          type="date"
                          value={editData.buchungstag}
                          onChange={(e) => setEditData({ ...editData, buchungstag: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Buchungstext</label>
                        <input
                          type="text"
                          value={editData.buchungstext}
                          onChange={(e) => setEditData({ ...editData, buchungstext: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                          placeholder="Buchungstext"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Verwendungszweck</label>
                        <textarea
                          value={editData.verwendungszweck}
                          onChange={(e) => setEditData({ ...editData, verwendungszweck: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors resize-none"
                          placeholder="Verwendungszweck"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Zahlungsbeteiligter</label>
                        <input
                          type="text"
                          value={editData.name_zahlungsbeteiligter}
                          onChange={(e) => setEditData({ ...editData, name_zahlungsbeteiligter: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                          placeholder="Name des Zahlungsbeteiligten"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Erstellt am</label>
                        <input
                          type="date"
                          value={editData.erstellt_am}
                          onChange={(e) => setEditData({ ...editData, erstellt_am: e.target.value })}
                          className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex justify-between items-start gap-4">
                        <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">Buchungstext</span>
                        <span className="text-sm text-gray-900 dark:text-white text-right">{statement.buchungstext || '-'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-500 dark:text-gray-400 block mb-1">Verwendungszweck</span>
                        <p className={`text-sm break-words ${statement.verwendungszweck ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500 italic'}`}>
                          {statement.verwendungszweck || 'Kein Verwendungszweck'}
                        </p>
                      </div>
                      {statement.name_zahlungsbeteiligter && (
                        <div className="flex justify-between items-start gap-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">Zahlungsbeteiligter</span>
                          <span className="text-sm text-gray-900 dark:text-white text-right">{statement.name_zahlungsbeteiligter}</span>
                        </div>
                      )}
                      {statement.erstellt_am && (
                        <div className="flex justify-between items-start gap-4">
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">Erstellt am</span>
                          <span className="text-sm text-gray-900 dark:text-white text-right">{formatDate(statement.erstellt_am)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <hr className="border-gray-100 dark:border-gray-700/50" />

                {/* Categories Section - nur bei normalen Buchungen */}
                {!isTransfer() && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                        <TagIcon className="w-3.5 h-3.5" />
                        Kategorien ({categories.length})
                      </h4>
                      {status === 'complete' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-success-50 dark:bg-success-900/20 text-success-600 dark:text-success-400">
                          Vollständig
                        </span>
                      )}
                      {status === 'partial' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-warning-50 dark:bg-warning-900/20 text-warning-600 dark:text-warning-400">
                          Teilweise
                        </span>
                      )}
                      {status === 'open' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          Offen
                        </span>
                      )}
                    </div>

                    {totalCategorized > 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatCurrency(totalCategorized)} von {formatCurrency(Math.abs(statement.betrag))} kategorisiert
                      </p>
                    )}

                    {/* Warning for incomplete categorization */}
                    {status === 'partial' && remainingAmount > 0 && (
                      <div className="flex items-start gap-2 p-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800">
                        <ExclamationTriangleIcon className="w-5 h-5 text-warning-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-warning-800 dark:text-warning-300">
                            {formatCurrency(remainingAmount)} nicht kategorisiert
                          </p>
                          <p className="text-xs text-warning-700 dark:text-warning-400 mt-0.5">
                            Bitte weitere Kategorien zuweisen
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Category Items */}
                    {categories.length > 0 ? (
                      <div className="space-y-2">
                        {categories.map((cat) => {
                          const Icon = getCategoryIcon(cat.kategorie_id);
                          return (
                            <div key={cat.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  isExpense ? 'bg-error-100 dark:bg-error-900/30' : 'bg-success-100 dark:bg-success-900/30'
                                }`}>
                                  <Icon className={`w-4 h-4 ${isExpense ? 'text-error-500' : 'text-success-500'}`} />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {getCategoryName(cat.kategorie_id)}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {cat.kategorie_id === 1 && cat.member_id
                                      ? `${getMemberName(cat.member_id)}${cat.beitrag_jahr ? ` (${cat.beitrag_jahr})` : ''}`
                                      : formatCurrency(cat.betrag)
                                    }
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => deleteCategory(cat.id)}
                                className="p-1 text-gray-400 hover:text-error-500 transition-colors"
                              >
                                <XMarkIcon className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : !isAddingCategory && (
                      /* Empty state for categories */
                      <div className="border border-dashed border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                        <TagIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Keine Kategorien zugewiesen</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Bitte Transaktion kategorisieren</p>
                      </div>
                    )}

                    {/* Add Category Form */}
                    {isAddingCategory && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4 border border-gray-200 dark:border-gray-600 space-y-3">
                        <div className="space-y-3">
                          {/* Category Dropdown */}
                          <div className="relative">
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Kategorie</label>
                            <button
                              onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                              className="w-full p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-left text-sm flex items-center justify-between hover:border-primary-400 transition-colors"
                            >
                              {selectedCategoryId ? (
                                <div className="flex items-center gap-2">
                                  {(() => {
                                    const Icon = getCategoryIcon(selectedCategoryId);
                                    return (
                                      <div className={`w-6 h-6 rounded flex items-center justify-center ${isExpense ? 'bg-error-100 dark:bg-error-900/30' : 'bg-success-100 dark:bg-success-900/30'}`}>
                                        <Icon className={`w-3.5 h-3.5 ${isExpense ? 'text-error-500' : 'text-success-500'}`} />
                                      </div>
                                    );
                                  })()}
                                  <span className="text-gray-900 dark:text-white">{getCategoryName(selectedCategoryId)}</span>
                                </div>
                              ) : (
                                <span className="text-gray-500 dark:text-gray-400">Kategorie wählen...</span>
                              )}
                              <ArrowDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
                            </button>
                            {showCategoryDropdown && (
                              <div ref={categoryDropdownRef} className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                                {sortedCategories.map((category) => {
                                  const Icon = getCategoryIcon(category.id);
                                  const isSelected = selectedCategoryId === category.id;
                                  return (
                                    <button
                                      key={category.id}
                                      onClick={() => {
                                        setSelectedCategoryId(category.id);
                                        setShowCategoryDropdown(false);
                                        if (category.id !== 1) setCategoryMemberId(null);
                                      }}
                                      className={`w-full p-3 text-left text-sm flex items-center gap-3 transition-colors ${
                                        isSelected
                                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                          : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                                        isSelected
                                          ? 'bg-primary-100 dark:bg-primary-900/30'
                                          : isExpense ? 'bg-error-100 dark:bg-error-900/30' : 'bg-success-100 dark:bg-success-900/30'
                                      }`}>
                                        <Icon className={`w-4 h-4 ${
                                          isSelected
                                            ? 'text-primary-500'
                                            : isExpense ? 'text-error-500' : 'text-success-500'
                                        }`} />
                                      </div>
                                      <span className="flex-1">{category.name}</span>
                                      {isSelected && (
                                        <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Amount Input */}
                          <div>
                            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Betrag</label>
                            <div className="relative">
                              <input
                                type="number"
                                value={categoryAmount}
                                onChange={(e) => setCategoryAmount(e.target.value)}
                                className="w-full px-3 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white hover:border-primary-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-colors"
                                step="0.01"
                                placeholder="0,00"
                              />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">EUR</span>
                            </div>
                          </div>

                          {/* Member Dropdown (for category 1) */}
                          {selectedCategoryId === 1 && (
                            <div className="relative">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mitglied</label>
                              <button
                                onClick={() => setShowMemberDropdown(!showMemberDropdown)}
                                className="w-full p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-left text-sm flex items-center justify-between hover:border-primary-400 transition-colors"
                              >
                                {categoryMemberId ? (
                                  <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                                      <UserIcon className="w-3.5 h-3.5 text-primary-500" />
                                    </div>
                                    <span className="text-gray-900 dark:text-white">{getMemberName(categoryMemberId)}</span>
                                  </div>
                                ) : (
                                  <span className="text-gray-500 dark:text-gray-400">Mitglied wählen...</span>
                                )}
                                <ArrowDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${showMemberDropdown ? 'rotate-180' : ''}`} />
                              </button>
                              {showMemberDropdown && (
                                <div ref={memberDropdownRef} className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-96 overflow-y-auto">
                                  {sortedMembers.map((member) => {
                                    const isSelected = categoryMemberId === member.member_id;
                                    return (
                                      <button
                                        key={member.member_id}
                                        onClick={() => {
                                          setCategoryMemberId(member.member_id);
                                          setShowMemberDropdown(false);
                                        }}
                                        className={`w-full p-3 text-left text-sm flex items-center gap-3 transition-colors ${
                                          isSelected
                                            ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                                            : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                                        }`}
                                      >
                                        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                                          isSelected ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-gray-700'
                                        }`}>
                                          <UserIcon className={`w-4 h-4 ${isSelected ? 'text-primary-500' : 'text-gray-500'}`} />
                                        </div>
                                        <span className="flex-1">{member.vorname} {member.name}</span>
                                        {isSelected && (
                                          <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Year Selector (for category 1 = Mitgliederbeiträge) */}
                          {selectedCategoryId === 1 && (
                            <div>
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Beitragsjahr</label>
                              <div className="flex items-center gap-2">
                                {[categoryBeitragJahr - 1, categoryBeitragJahr, categoryBeitragJahr + 1].map((year) => (
                                  <button
                                    key={year}
                                    type="button"
                                    onClick={() => setCategoryBeitragJahr(year)}
                                    className={`flex-1 py-2 px-3 text-sm rounded-lg border transition-colors ${
                                      categoryBeitragJahr === year
                                        ? 'bg-primary-600 text-white border-primary-600'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600 hover:border-primary-400'
                                    }`}
                                  >
                                    {year}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-2">
                            <button
                              onClick={() => {
                                setIsAddingCategory(false);
                                setSelectedCategoryId(0);
                                setCategoryAmount('');
                                setCategoryMemberId(null);
                                setCategoryBeitragJahr(new Date().getFullYear());
                              }}
                              className="flex-1 py-2.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                              Abbrechen
                            </button>
                            <button
                              onClick={addCategory}
                              disabled={!selectedCategoryId || !categoryAmount || (selectedCategoryId === 1 && !categoryMemberId)}
                              className="flex-1 py-2.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors font-medium"
                            >
                              Hinzufügen
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Category Button */}
                    {!isAddingCategory && remainingAmount > 0 && (
                      <button
                        onClick={() => {
                          setIsAddingCategory(true);
                          setCategoryAmount(remainingAmount.toFixed(2));
                          // Default Beitragsjahr: aktuelles Jahr
                          setCategoryBeitragJahr(new Date().getFullYear());
                        }}
                        className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        + Kategorie hinzufügen
                      </button>
                    )}
                  </div>
                )}

                {/* Divider */}
                {!isTransfer() && <hr className="border-gray-100 dark:border-gray-700/50" />}

                {/* Documents Section - nur bei normalen Buchungen */}
                {!isTransfer() && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <DocumentTextIcon className="w-3.5 h-3.5" />
                      Belege ({documents.length})
                    </h4>

                    {/* Document Items */}
                    {documents.length > 0 ? (
                      <div className="space-y-2">
                        {documents.map((doc) => {
                          const isImage = doc.file_type?.startsWith('image/') ||
                            doc.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ||
                            doc.rechnung_pfad?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                          return (
                            <div
                              key={doc.id}
                              onClick={() => handleDocumentClick(doc.id)}
                              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
                            >
                              {/* Thumbnail */}
                              <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-600 flex items-center justify-center flex-shrink-0">
                                {isImage ? (
                                  <PhotoIcon className="w-6 h-6 text-gray-400" />
                                ) : (
                                  <DocumentIcon className="w-6 h-6 text-gray-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {doc.name || doc.rechnung_pfad || 'Beleg'}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {isImage ? 'Bild' : 'PDF'} {doc.file_size ? `- ${Math.round(doc.file_size / 1024)} KB` : ''}
                                </p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteDocument(doc.id);
                                }}
                                className="p-1 text-gray-400 hover:text-error-500 flex-shrink-0 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Empty state for documents */
                      <div className="border border-dashed border-gray-200 dark:border-gray-600 rounded-lg p-6 text-center">
                        <DocumentTextIcon className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Keine Belege vorhanden</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">Klicken zum Hochladen oder hierher ziehen</p>
                      </div>
                    )}

                    {/* Add Document Button */}
                    <button
                      onClick={() => setShowInvoiceMatcher(true)}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      + Beleg hinzufügen
                    </button>
                  </div>
                )}

                {/* Divider */}
                <hr className="border-gray-100 dark:border-gray-700/50" />

                {/* Tags Section */}
                <div className="space-y-3">
                  <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                    <TagIcon className="w-3.5 h-3.5" />
                    Tags ({statementTags.length})
                  </h4>
                  {/* Active tags */}
                  <div className="flex flex-wrap gap-1.5">
                    {statementTags.map(tag => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border border-primary-300 dark:border-primary-700"
                      >
                        <CheckIcon className="w-3 h-3" />
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-0.5 text-primary-400 hover:text-error-500 transition-colors"
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => setShowTagPicker(!showTagPicker)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                        showTagPicker
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 border-primary-300 dark:border-primary-700'
                          : 'text-gray-500 dark:text-gray-400 border-dashed border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <PlusIcon className={`w-3 h-3 transition-transform ${showTagPicker ? 'rotate-45' : ''}`} />
                      Tags
                    </button>
                  </div>
                  {/* Tag picker */}
                  {showTagPicker && (
                    <div className="p-2.5 bg-gray-50 dark:bg-gray-700/30 rounded-xl border border-gray-100 dark:border-gray-600">
                      {availableBuchungTags.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {availableBuchungTags.filter(t => !statementTags.includes(t)).map(tag => (
                            <button
                              key={tag}
                              onClick={() => handleAddTag(tag)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-primary-50 hover:text-primary-700 hover:border-primary-300 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 dark:hover:border-primary-700 transition-colors"
                            >
                              <PlusIcon className="w-3 h-3" />
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={newTagInput}
                          onChange={(e) => setNewTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && newTagInput.trim()) {
                              handleAddTag(newTagInput.trim());
                              setNewTagInput('');
                            }
                            if (e.key === 'Escape') {
                              setShowTagPicker(false);
                              setNewTagInput('');
                            }
                          }}
                          placeholder="Neuer Tag..."
                          className="flex-1 px-2.5 py-1 text-xs text-gray-900 dark:text-white bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent placeholder-gray-400 dark:placeholder-gray-500"
                        />
                        {newTagInput.trim() && (
                          <button
                            onClick={() => { handleAddTag(newTagInput.trim()); setNewTagInput(''); }}
                            className="p-1.5 bg-primary-600 text-white rounded-full hover:bg-primary-700 transition-colors"
                          >
                            <CheckIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Transfer Details - nur bei Umbuchungen */}
                {isTransfer() && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider flex items-center gap-1.5">
                      <ArrowsRightLeftIcon className="w-3.5 h-3.5" />
                      Umbuchungsdetails
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600">
                        <div className="w-8 h-8 bg-error-100 dark:bg-error-900/30 rounded-lg flex items-center justify-center">
                          <ArrowUpIcon className="w-4 h-4 text-error-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Von Konto</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {getAccountName(statement.von_konto_id)}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-error-500">
                          -{formatCurrency(Math.abs(statement.betrag))}
                        </span>
                      </div>

                      <div className="flex justify-center">
                        <ArrowDownIcon className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-600">
                        <div className="w-8 h-8 bg-success-100 dark:bg-success-900/30 rounded-lg flex items-center justify-center">
                          <ArrowDownIcon className="w-4 h-4 text-success-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400">Nach Konto</p>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {getAccountName(statement.nach_konto_id)}
                          </p>
                        </div>
                        <span className="text-sm font-medium text-success-500">
                          +{formatCurrency(Math.abs(statement.betrag))}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer: Löschen LINKS, Schließen RECHTS */}
          {statement && !loading && (
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/50 flex items-center justify-between">
              {isEditing ? (
                <>
                  <button
                    onClick={cancelEdit}
                    className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={saveEdit}
                    disabled={isSaving}
                    className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl transition-colors text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  >
                    <CheckIcon className="w-4 h-4" />
                    {isSaving ? 'Speichern...' : 'Speichern'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleDeleteClick}
                    className="px-5 py-2.5 bg-error-600 hover:bg-error-700 text-white rounded-xl transition-colors text-sm font-medium flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Löschen
                  </button>
                  <button
                    onClick={handleClose}
                    className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                  >
                    Schließen
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        visible={confirmModal.visible}
        onClose={() => setConfirmModal({ ...confirmModal, visible: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, visible: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, visible: false })
          },
          confirm: {
            text: 'Löschen',
            onPress: () => {
              confirmDeleteStatement();
              setConfirmModal({ ...confirmModal, visible: false });
            }
          }
        } : undefined}
      />

      {/* Invoice Matcher Modal */}
      {showInvoiceMatcher && (
        <InvoiceMatcher
          onFilesSelected={handleFilesSelected}
          initialAmount={Math.abs(statement?.betrag || 0)}
          preSelectedDocuments={documents.map(doc => doc.id)}
          onClose={() => setShowInvoiceMatcher(false)}
        />
      )}

      {/* Document Detail Modal */}
      {showDocumentDetail && (
        <DocumentDetailModal
          documentId={showDocumentDetail}
          onClose={() => setShowDocumentDetail(null)}
          onDelete={handleDocumentDelete}
          onTagAdd={handleDocumentTagAdd}
          onTagDelete={handleDocumentTagDelete}
          onAIScan={handleDocumentAIScan}
          onDownload={handleDocumentDownload}
        />
      )}
    </>
  );
};

export default StatementDetailModal;
